import { stringify } from 'qs';
import axios from 'axios';
import { useNuxtApp, useRuntimeConfig, useRequestEvent } from 'nuxt/app';
import { caseInsensitiveKeyReplace } from '~~/common/utils/headerHelpers.js';
import { HEADER_X_AW_PERF_UUID } from 'awFeConsole/util/anyPlatform';
import type { CreateAxiosDefaults, AxiosInstance, AxiosHeaderValue, AxiosResponse, AxiosRequestConfig } from 'axios';

export const createMsgTokenNetwork = (proxiedHeaders: Record<string, unknown>): AwExtendedAxiosInstance => {
  const { $awRuntimeConfig, $i18n } = useNuxtApp();

  return extendNetwork(axios.create({
    baseURL: $awRuntimeConfig.messageTokenURL,
    headers: caseInsensitiveKeyReplace(
      proxiedHeaders,
      {
        'Accept-Language': $i18n.locale.value,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    ),
    responseType: 'json',
    paramsSerializer,
    validateStatus,
  }));
};

export const createFeApiNetwork = (proxiedHeaders: Record<string, unknown>): AwExtendedAxiosInstance => {
  const { $i18n, $awRuntimeConfig } = useNuxtApp();
  return extendNetwork(axios.create({
    baseURL: $awRuntimeConfig.feApiNetworkBaseURL,
    headers: caseInsensitiveKeyReplace(
      proxiedHeaders,
      {
        'Accept-Language': $i18n.locale.value,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    ),
    responseType: 'json',
    paramsSerializer,
    validateStatus,
  }));
};

export const createBeAuthenticationNetwork = (proxiedHeaders: Record<string, unknown>): AwExtendedAxiosInstance => {
  const { $i18n, $awMergedConfig } = useNuxtApp();
  const config = useRuntimeConfig();

  const authorizationURL = config[`auchanAuthorizationHost${__AW_IS_SHOP__ ? 'Shop' : 'Dealer'}`];
  return extendNetwork(axios.create({
    baseURL: `${authorizationURL}${$awMergedConfig.auchanAuthorizationPrefix}`,
    headers: caseInsensitiveKeyReplace(
      proxiedHeaders,
      {
        'Accept-Language': $i18n.locale.value,
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
      },
    ),
    responseType: 'json',
    paramsSerializer,
    validateStatus,
  }));
};

export const createApiNetwork = (proxiedHeaders: Record<string, unknown>): AwExtendedAxiosInstance => {
  const { $i18n, $awRuntimeConfig } = useNuxtApp();
  return extendNetwork(axios.create({
    baseURL: $awRuntimeConfig.apiNetworkBaseURL,
    headers: caseInsensitiveKeyReplace(
      proxiedHeaders,
      {
        'Accept-Language': $i18n.locale.value,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    ),
    responseType: 'json',
    paramsSerializer,
    validateStatus,
  }));
};

function validateStatus (status: number): boolean {
  return status < 400;
}
function paramsSerializer (params?: CreateAxiosDefaults['paramsSerializer']) {
  return stringify(params, {
    arrayFormat: 'brackets',
  });
}

type AxiosMethod = 'request' | 'delete' | 'get' | 'head' | 'options' | 'post' | 'put' | 'patch';

// The `$`-prefixed helpers unwrap the response and return `res.data` directly.
// `T` is the type of that `data` payload (not the full AxiosResponse); it defaults
// to `unknown` so callers must narrow it, or pass an explicit type: `$get<UserModel>(url)`.

// query-style: url + optional config, no request body — get, delete, head, options
interface AwAxiosQueryMethod {
  <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>,
}
// body-style: url + optional body + optional config — post, put, patch
interface AwAxiosBodyMethod {
  <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>,
}
// request-style: a single config object — request
interface AwAxiosRequestMethod {
  <T = unknown>(config: AxiosRequestConfig): Promise<T>,
}

export interface AwExtendedAxiosInstance extends AxiosInstance {
  $request: AwAxiosRequestMethod,
  $delete: AwAxiosQueryMethod,
  $get: AwAxiosQueryMethod,
  $head: AwAxiosQueryMethod,
  $options: AwAxiosQueryMethod,
  $post: AwAxiosBodyMethod,
  $put: AwAxiosBodyMethod,
  $patch: AwAxiosBodyMethod,
  setHeader (this: AxiosInstance, name: string, value: AxiosHeaderValue): void,
}
function useAxiosDollarFunction (axiosInstance: AxiosInstance) {
  return function (method: AxiosMethod) {
    return async function (
      ...params: unknown[]
    ) {
      const res = await (axiosInstance[method] as (...args: unknown[]) => Promise<AxiosResponse>).bind(axiosInstance)(
        ...params,
      );
      return res && res.data;
    };
  };
}

function extendNetwork (network: AxiosInstance): AwExtendedAxiosInstance {
  const modifiedNetwork = network as AwExtendedAxiosInstance;
  // The perf uuid header is only meaningful server-side (it correlates with the
  // nitro PerformanceObserver in awPerf.ts), so guard on `import.meta.server`.
  if (import.meta.server && useRuntimeConfig().public.awIsDbgTimerEnabled) {
    const awPerfUuid = useRequestEvent()?.context.awPerf?.uuid;
    modifiedNetwork.interceptors.request.use((config) => {
      config.headers[HEADER_X_AW_PERF_UUID] = awPerfUuid;
      return config;
    });
  }
  modifiedNetwork.setHeader = (
    ((function (
      this: AxiosInstance,
      name: string,
      value: AxiosHeaderValue,
    ): void {
      if (!value) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.defaults.headers[name];
      }
      this.defaults.headers[name] = value;
    })
      .bind(modifiedNetwork)
    )
  );
  const axiosDollarFunction = useAxiosDollarFunction(modifiedNetwork);
  modifiedNetwork.$request = axiosDollarFunction('request');
  modifiedNetwork.$delete = axiosDollarFunction('delete');
  modifiedNetwork.$get = axiosDollarFunction('get');
  modifiedNetwork.$head = axiosDollarFunction('head');
  modifiedNetwork.$options = axiosDollarFunction('options');
  modifiedNetwork.$post = axiosDollarFunction('post');
  modifiedNetwork.$put = axiosDollarFunction('put');
  modifiedNetwork.$patch = axiosDollarFunction('patch');

  return modifiedNetwork;
}
